/**
 * The order the bars are actually played in.
 *
 * Written music is not a list of bars to be read once from the top: a repeat sends the
 * player back, a first-time bar is skipped on the way through the second time, a D.S. sends
 * the player back to a sign and from there to a coda. Playback, anything that counts
 * elapsed time and the following of a tie into a second-time bar have to know that order,
 * and the notation alone does not state it -- it has to be worked out.
 *
 * Two kinds of sign are read here. A **repeat** is a pair of walls around the music between
 * them, with first- and second-time bars; what is supported is the ordinary case. Nested
 * repeats are not, in the same way that staff groups do not nest in v1: a repeat inside a
 * repeat needs a stack and a rule for which end belongs to which start, and getting that
 * wrong silently plays the wrong music. A file that has them is played through once per
 * repeat mark rather than guessed at.
 *
 * How many times a repeated section is played is the count its repeat end states, and where
 * it states none is read off the two places the notation numbers the passes: its ending bars
 * and the verses sung under it. A section whose endings are `1, 2` and then `3` is played
 * three times, one with four verses under it four times, and one with neither twice.
 *
 * A **jump** -- D.C., D.S., To Coda, Fine -- is not a pair of walls but a word at one bar
 * and a sign at another. Three conventions decide what it does, and each is a line of this
 * file: a jump sign is obeyed once and read over ever after, the coda and the Fine mean
 * nothing until a jump has been taken, and the pass after a jump does not take the repeats
 * again. That last one is what "D.C. senza repetizione" states explicitly and what every
 * player does without being told.
 */

import type { Score } from "./types";

/**
 * A safety limit on how many bars a piece can play for.
 *
 * A malformed document -- a backward repeat before its forward one, an ending numbered for
 * a pass that never happens -- can describe a loop with no way out, and a count or an ending
 * number in the hundreds of thousands one with no end worth waiting for. This bounds it at a
 * length no real piece reaches, so a bad file plays something wrong rather than hanging.
 */
const LIMIT = 100_000;

/** The first bar carrying a sign of its own, or -1 when the piece carries none. */
function signedAt(score: Score, sign: "segno" | "coda"): number {
  return score.measures.findIndex((measure) => measure.jump?.[sign] === true);
}

/**
 * The ending numbers of the run of ending bars `index` belongs to, or that begins at the bar
 * after it; none where there is no such run.
 *
 * The bar after counts because a repeat end need not stand in an ending of its own: a wall
 * with nothing over it and an ending `3` behind it is a section played three times, the last
 * of them going on into that ending.
 */
function endingsAround(score: Score, index: number): readonly number[] {
  const numbers: number[] = [];
  for (const step of [-1, 1]) {
    for (let at = step < 0 ? index : index + 1; at >= 0 && at < score.measures.length; at += step) {
      const endings = score.measures[at]?.repeat?.endings ?? [];
      if (endings.length === 0) break;
      numbers.push(...endings);
    }
  }
  return numbers;
}

/**
 * The ending a pass plays, of the numbers its run carries, or 0 for none of them.
 *
 * The one numbered for it. Where none is, the nearest numbered before it: a bracket printed
 * "1. u. 2." and numbered 1 with a 3 after it is a file that exists, the wall the second pass
 * has to go back from stands in that bracket, and skipping it would leave the 3 never reached.
 * Past the highest number none is played, the run having been written for fewer passes than
 * the section is being given.
 */
function endingFor(numbers: readonly number[], pass: number): number {
  if (pass > Math.max(0, ...numbers)) return 0;
  return Math.max(0, ...numbers.filter((number) => number <= pass));
}

/**
 * How many verses are sung under the bars `from` to `to`: the highest-numbered verse with
 * words in more than half as many of those bars as the verse with words in most of them.
 *
 * A strophic song prints its verses one under another with a bare repeat sign round them, and
 * is sung through once for each. A line of words under a few of the bars only is not a verse
 * -- another reading of a phrase, the changed words of a refrain -- and sends nobody round
 * again.
 */
function versesUnder(score: Score, from: number, to: number): number {
  const bars = new Map<number, number>();
  for (let index = from; index <= to; index += 1) {
    const id = score.measures[index]?.id;
    if (id === undefined) continue;
    const sung = new Set<number>();
    for (const part of score.parts) {
      for (const voice of part.measures[id]?.voices ?? []) {
        for (const event of voice.events) {
          if (event.kind !== "note") continue;
          for (const lyric of event.lyrics) sung.add(lyric.verse);
        }
      }
    }
    for (const verse of sung) bars.set(verse, (bars.get(verse) ?? 0) + 1);
  }
  const most = Math.max(0, ...bars.values());
  return Math.max(0, ...[...bars].filter(([, held]) => held * 2 > most).map(([verse]) => verse));
}

/**
 * Whether the wall at `index` is gone back from to `start`, having been gone back from `back`
 * times and the section being on its pass `pass`.
 *
 * A wall that states its count is gone back from one time fewer than that: that is the
 * engraver saying it outright, whatever is written under it. A wall that states none is gone
 * back from once, and then for as long as the endings at it or the verses under its section
 * number a pass still to come: endings `1, 2` and then `3` are three times through, a wall
 * with a lone ending `11` behind it is eleven, and four verses under a bare repeat are four.
 * The once is counted by the wall rather than by the pass, because two walls may share one
 * start and the pass counts both -- and the passes still to come by the pass rather than by
 * the wall, for the same reason: endings `1, 3` and `2, 4`, each with a wall of its own, are
 * four times through and not seven.
 */
function goesBack(score: Score, index: number, start: number, back: number, pass: number): boolean {
  const stated = score.measures[index]?.repeat?.times ?? null;
  if (stated !== null) return back < stated - 1;
  if (back < 1) return true;
  return pass < Math.max(0, ...endingsAround(score, index), versesUnder(score, start, index));
}

/**
 * The indices of {@link Score.measures}, in playing order.
 *
 * A bar appears as many times as it is played, so the answer is longer than the score and
 * an index may repeat. A caller that wants the written order rather than the played one
 * passes `false` and gets each bar once.
 */
export function playOrder(score: Score, repeats = true): readonly number[] {
  if (!repeats) return [...Array(score.measures.length).keys()];

  const order: number[] = [];
  const taken = new Map<number, number>();
  const jumped = new Set<number>();
  let start = 0;
  let pass = 1;
  let index = 0;
  // Set by the first D.C. or D.S. taken, and never unset: everything after it is the last
  // time through, which is what makes a Fine an ending and a coda sign a destination.
  let returning = false;

  while (index < score.measures.length && order.length < LIMIT) {
    const measure = score.measures[index];
    const repeat = measure?.repeat ?? null;
    const jump = measure?.jump ?? null;

    if (!returning && repeat?.start === true && index !== start) {
      start = index;
      pass = 1;
    }

    const endings = repeat?.endings ?? [];
    if (endings.length > 0) {
      const numbers = endingsAround(score, index);
      // Coming back from a D.C. the player goes straight on to the end, which at a first- and
      // second-time bar means the last of them: the first-time bar exists to be played on the
      // way to a repeat, and there is no repeat left to go to.
      const wanted = returning ? Math.max(...numbers) : endingFor(numbers, pass);
      if (!endings.includes(wanted)) {
        index += 1;
        continue;
      }
    }

    order.push(index);

    if (returning && jump?.fine === true) break;

    if (returning && jump?.toCoda === true) {
      const coda = signedAt(score, "coda");
      if (coda >= 0) {
        index = coda;
        continue;
      }
    }

    const back = taken.get(index) ?? 0;
    if (!returning && repeat?.end === true && goesBack(score, index, start, back, pass)) {
      taken.set(index, back + 1);
      pass += 1;
      index = start;
      continue;
    }

    if (jump !== null && jump.jump !== null && !jumped.has(index)) {
      const target = jump.jump === "daCapo" ? 0 : signedAt(score, "segno");
      // A D.S. in a piece with no segno has nowhere to go. It is read over rather than
      // guessed at as a D.C., which would play a section nobody asked for.
      if (target >= 0) {
        jumped.add(index);
        returning = true;
        index = target;
        continue;
      }
    }

    index += 1;
  }

  return order;
}
